import React, { useEffect, useRef, useState } from 'react';
import { Link } from "react-router-dom";
import _ from "lodash";
import { useMutation } from "@tanstack/react-query";
import { loginGoogle } from "../../../api/public-voissa/users";
import createLink from "../../../libs/createLink";
import TextError from '../UI/TextError/TextError';
import './GoogleSignin.scss';

// ID client du projet Google (public). Absent (ex. build experimental, origine non déclarée chez
// Google) → aucun bouton affiché.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GSI_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

// Le script Google n'est chargé qu'au premier bouton affiché, puis réutilisé.
let gsiScriptPromise = null;
function loadGsiScript() {
    if (window.google?.accounts?.id) return Promise.resolve();
    if (!gsiScriptPromise) {
        gsiScriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = GSI_SCRIPT_URL;
            script.async = true;
            script.onload = resolve;
            script.onerror = () => {
                gsiScriptPromise = null;
                reject(new Error('GSI_LOAD_FAILED'));
            };
            document.head.appendChild(script);
        });
    }
    return gsiScriptPromise;
}

// Google n'accepte qu'une initialisation par page : la preuve reçue est transmise au bouton
// cliqué en dernier (la page de connexion et la fenêtre d'inscription peuvent coexister).
let currentCredentialHandler = () => {};
let isGsiInitialized = false;
function initializeGsi() {
    if (isGsiInitialized) return;
    window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => currentCredentialHandler(response.credential),
        ux_mode: 'popup',
        auto_select: false
    });
    isGsiInitialized = true;
}

// Logo « G » de Google, pour rappeler le compte utilisé.
export const GoogleLogo = (props) => (
    <svg viewBox="0 0 48 48" aria-hidden="true" {...props}>
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
);

const errorMessage = (error) => {
    if (error?.statusCode === 429) return "Trop de demandes, veuillez réessayer dans 10 minutes";
    if (error?.message && !["Unauthorized", "Bad Request", "Conflict"].includes(error.message)) return error.message;
    return "La connexion avec Google n'a pas abouti, veuillez réessayer.";
};

/**
 * Bouton « Continuer avec Google ». Envoie la preuve Google à l'API et remet sa réponse au parent
 * (`onResponse`), avec la preuve elle-même (`credential`) pour finir une inscription :
 * - { token, rules }  → connecté ;
 * - { requiresMfa, … } → double authentification (admins) ;
 * - { requiresSignup, email } → aucun compte : afficher la fin d'inscription.
 */
const GoogleSignin = ({ onResponse = () => {}, text = "continue_with" }) => {
    const buttonRef = useRef(null);
    const [isUnavailable, setIsUnavailable] = useState(false);

    const { isLoading, error, isError, mutate } = useMutation(credential => loginGoogle({ credential }), {
        onSuccess: (data, credential) => onResponse({ ...data, credential })
    });
    const mutateRef = useRef(mutate);
    mutateRef.current = mutate;

    useEffect(() => {
        if (!GOOGLE_CLIENT_ID) return;
        let cancelled = false;
        const handler = (credential) => mutateRef.current(credential);

        loadGsiScript()
            .then(() => {
                if (cancelled || !buttonRef.current) return;
                initializeGsi();
                currentCredentialHandler = handler;
                // renderButton ajoute un bouton sans retirer le précédent (effet rejoué) : on vide d'abord.
                buttonRef.current.innerHTML = '';
                window.google.accounts.id.renderButton(buttonRef.current, {
                    type: 'standard',
                    theme: 'outline',
                    size: 'large',
                    shape: 'pill',
                    text,
                    locale: 'fr',
                    click_listener: () => { currentCredentialHandler = handler; },
                    // Google impose une largeur fixe, entre 200 et 400 px.
                    width: Math.max(200, Math.min(400, buttonRef.current.offsetWidth || 320))
                });
            })
            .catch(() => {
                if (!cancelled) setIsUnavailable(true);
            });

        return () => {
            cancelled = true;
            if (currentCredentialHandler === handler) currentCredentialHandler = () => {};
        };
    }, [text]);

    if (!GOOGLE_CLIENT_ID || isUnavailable) return null;

    const banError = isError && error?.statusCode === 400 && _.get(error, "errors.isBan");

    return (
        <div className="google-signin">
            <div className="google-signin-button" ref={buttonRef} />
            {isLoading && <div className="google-signin-loading">Connexion avec Google…</div>}
            {banError ? (
                <TextError show center>
                    Votre profil a été banni de Voissa{error.message && error.message !== "Bad Request" ? ` pour la raison suivante "${error.message}"` : ""}<br />
                    Pour toute question veuillez <Link to={createLink("contact")}>nous contacter</Link>
                </TextError>
            ) : (
                <TextError show={isError} center>{errorMessage(error)}</TextError>
            )}
            <div className="google-signin-separator"><span>ou</span></div>
        </div>
    );
};

export default GoogleSignin;
